import { isArchived } from './gameStatus';

/** The league a game belongs to, or null for a one-off. */
export const leagueIdOf = (game) => game.miniLeagueTournament?.miniLeague?.id ?? null;

const key = (id) => String(id);

/**
 * Fold the games list and the leagues list into the shape the page renders:
 * a group per league, then whatever is left over as one-off games.
 *
 * Two rules worth keeping:
 *
 * A league with no games still gets a group. That is a real state - a league exists
 * from the moment it is created, before its manager has added a tournament - and it
 * is the only place the manager would be prompted to finish the job.
 *
 * A game whose league is missing from `leagues` builds its own group rather than
 * disappearing. The leagues call can fail on its own while the games call succeeds,
 * and a list that silently drops half a player's games is far worse than one showing
 * a header with nothing but a name on it.
 */
export function groupGames(games = [], leagues = []) {
  const groups = new Map();
  leagues.forEach((league) => groups.set(key(league.id), { league, games: [] }));

  const standalone = [];

  games.forEach((game) => {
    const leagueId = leagueIdOf(game);
    if (leagueId == null) {
      standalone.push(game);
      return;
    }
    let group = groups.get(key(leagueId));
    if (!group) {
      group = { league: { ...game.miniLeagueTournament.miniLeague, partial: true }, games: [] };
      groups.set(key(leagueId), group);
    }
    group.games.push(game);
  });

  return { leagueGroups: Array.from(groups.values()), standalone };
}

/**
 * Leagues archive too, and on a longer clock than games - thirty days against seven,
 * because a game is one week of one competition and its result is stale in days, while
 * the table at the end of a season is the thing the group actually talks about.
 *
 * Both flags are the API's, from LeagueArchivePolicy and GameArchivePolicy. Neither is
 * recomputed here. The league one asks `endedAt`, so a cancelled league ages out on the
 * same clock as a completed one - which it has to, since a league that was called off
 * never completes and would otherwise sit in the list for ever.
 */
export const isLeagueArchived = (league) => league?.archived === true;

/**
 * Split everything into what the page shows by default and what hides behind the
 * disclosure.
 *
 * A game inside an archived league goes with it. Ordinarily its own timestamp has
 * already put it there - a league cannot end before its games do - but the two windows
 * differ, so this is stated rather than assumed: a game left behind by a hidden league
 * would rebuild a group of its own out of the league it names, and a league nobody can
 * see would reappear as a heading.
 */
export function partitionArchived(games = [], leagues = []) {
  const archivedLeagueIds = new Set(
    leagues.filter(isLeagueArchived).map((league) => key(league.id))
  );
  const gameIsArchived = (game) => {
    const leagueId = leagueIdOf(game);
    return isArchived(game) || (leagueId != null && archivedLeagueIds.has(key(leagueId)));
  };

  return {
    current: {
      games: games.filter((game) => !gameIsArchived(game)),
      leagues: leagues.filter((league) => !isLeagueArchived(league)),
    },
    archived: {
      games: games.filter(gameIsArchived),
      leagues: leagues.filter(isLeagueArchived),
    },
  };
}
